/**
 * Testimonials — 4 variants (carousel, wall_3col, spotlight_video, photo_spotlight)
 */
import { useState } from 'react';
import type { TestimonialsContent } from '../schemas/testimonials';
import {
  Avatar,
  Body,
  Eyebrow,
  H2,
  PlayIcon,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';

interface TP {
  content: TestimonialsContent;
}

function Stars({ rating }: { rating?: string | null }) {
  const n = rating ? Math.max(0, Math.min(5, parseInt(rating, 10) || 0)) : 0;
  if (n === 0) return null;
  return (
    <div style={{ color: 'var(--ls-accent)', display: 'flex', gap: 2 }}>
      {Array.from({ length: n }).map((_, i) => (
        <span key={i}>★</span>
      ))}
    </div>
  );
}

export function TestimonialsCarousel({ content }: TP) {
  const [idx, setIdx] = useState(0);
  const t = content.testimonials[idx];
  if (!t) return null;
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, alignItems: 'center', textAlign: 'center', maxWidth: 800, margin: '0 auto' }}>
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        {content.headline && <H2>{content.headline}</H2>}
        <Stars rating={t.rating} />
        <blockquote
          style={{
            fontFamily: 'var(--ls-font-heading)',
            fontSize: 'clamp(1.125rem, 2vw + 0.5rem, 1.75rem)',
            margin: 0,
            lineHeight: 1.4,
          }}
        >
          {t.quote}
        </blockquote>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center' }}>
          <Avatar src={t.avatar} name={t.name} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600 }}>{t.name}</div>
            {(t.role || t.company) && (
              <div style={{ fontSize: '0.85rem', color: 'var(--ls-text-muted)' }}>
                {[t.role, t.company].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </div>
        {content.testimonials.length > 1 && (
          <div style={{ display: 'flex', gap: 8 }}>
            {content.testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  border: 'none',
                  cursor: 'pointer',
                  background: i === idx ? 'var(--ls-accent)' : 'var(--ls-border)',
                  padding: 0,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </SectionFrame>
  );
}

export function TestimonialsWall3Col({ content }: TP) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && <H2>{content.headline}</H2>}
        </div>
        <div className="ls-grid ls-grid-3">
          {content.testimonials.map((t, i) => (
            <div key={i} className="ls-card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Stars rating={t.rating} />
              <Body style={{ fontSize: '0.95rem', color: 'var(--ls-text)' }}>{t.quote}</Body>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 'auto' }}>
                <Avatar src={t.avatar} name={t.name} size={36} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t.name}</div>
                  {(t.role || t.company) && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--ls-text-muted)' }}>
                      {[t.role, t.company].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function TestimonialsSpotlightVideo({ content }: TP) {
  const t = content.testimonials[0];
  if (!t) return null;
  return (
    <SectionFrame background="surface">
      <div className="ls-split-pair">
        <div className="ls-split-media" style={{ position: 'relative', borderRadius: 'var(--ls-radius-lg)', overflow: 'hidden' }}>
          <VisualPlaceholder src={t.avatar} aspectRatio="4 / 5" label={t.name} />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.95)',
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlayIcon size={28} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <Stars rating={t.rating} />
          <blockquote style={{ fontFamily: 'var(--ls-font-heading)', fontSize: '1.5rem', margin: 0, lineHeight: 1.4 }}>
            {t.quote}
          </blockquote>
          <div>
            <div style={{ fontWeight: 600 }}>{t.name}</div>
            {(t.role || t.company) && (
              <div style={{ fontSize: '0.85rem', color: 'var(--ls-text-muted)' }}>
                {[t.role, t.company].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </div>
      </div>
    </SectionFrame>
  );
}

export function TestimonialsPhotoSpotlight({ content }: TP) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && <H2>{content.headline}</H2>}
        </div>
        <div className="ls-grid ls-grid-3">
          {content.testimonials.slice(0, 3).map((t, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <VisualPlaceholder src={t.avatar} aspectRatio="3 / 4" label={t.name} />
              <Body style={{ fontSize: '0.9rem' }}>{t.quote}</Body>
              <div>
                <div style={{ fontWeight: 600 }}>{t.name}</div>
                {(t.role || t.company) && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--ls-text-muted)' }}>
                    {[t.role, t.company].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export const testimonialsVariants = {
  carousel: TestimonialsCarousel,
  wall_3col: TestimonialsWall3Col,
  spotlight_video: TestimonialsSpotlightVideo,
  photo_spotlight: TestimonialsPhotoSpotlight,
};
